/* eslint-disable no-useless-escape */
import { find } from "lodash";
import template from "./number-field.html";
import "./number-field.less";

import {
	EVENT_SET_FORM_DIRTY,
	EVENT_VALUE_CHANGED
} from "constants.js";
import {
	roundToNearestIncrement,
	roundToRoundingPoint,
	isValidCompleteNumber,
	isValidPartialNumber,
	isNumberWithinConstraints,
	canCompleteNumberWithinConstraints,
	getNextConstrainedValue,
	getPreviousConstrainedValue,
	getNumericConstraintMessage
} from "services/math-service.js";

export class Controller {
	lookups = [];

	constructor(eventBus, $logger) {
		this.$logger = $logger;

		this.trailingComma = /^[1-9]\d*(\.\d+)?$/;
		this.incrementingStep = 1;
		this.digitCount = 0;
		this.editValue = "";
		this.previousEditValue = "";
		this.committedValueBeforeEdit = "";
		this.numberConstraintMessage = "";
		this.numberConstraintMessageType = "";
		this.isFocused = false;
		this.hasRejectedRangeEdit = false;
	}

	$onInit() {
		this._eventBus = this.reportScope.events;
		this.initialFieldValue = this.fieldValue?.number?.toString() ?? "";
		this.editValue = this.initialFieldValue;
		this.previousEditValue = this.editValue;
		this.committedValueBeforeEdit = this.editValue;

		if(this.field.IncrementingStep) {
			this.incrementingStep = this.field.IncrementingStep;
			const decimalSplit = `${this.field.IncrementingStep}`.split(".");
			this.digitCount = decimalSplit[1] ? decimalSplit[1].length : 0;
		}
	}

	$doCheck() {
		if(this.isFocused) {
			return;
		}

		const externalValue = this.fieldValue?.number?.toString() ?? "";
		if(externalValue !== this.editValue) {
			this.syncEditValue(externalValue);
		}
	}

	onFocus() {
		this.isFocused = true;
		this.lastSetValue = this.fieldValue?.number?.toString() ?? "";
		this.committedValueBeforeEdit = this.fieldValue?.number?.toString() ?? "";
		this.editValue = this.committedValueBeforeEdit;
		this.previousEditValue = this.editValue;
		this.clearNumberConstraintMessage();
		this.hasRejectedRangeEdit = false;
	}

	increment() {
		this._eventBus.trigger(EVENT_SET_FORM_DIRTY);

		const factor = this.fieldValue.unit && this.fieldValue.unit.Factor || 1;
		if(this.field.MaxValue !== undefined && this.editValue >= Math.floor(this.field.MaxValue / factor)) {
			this.setSpinnerValue(Math.floor(this.field.MaxValue / factor));
			return;
		}

		const value = getNextConstrainedValue(
			this.editValue,
			this.getNumericConstraints(),
			this.incrementingStep,
			this.digitCount
		);
		this.setSpinnerValue(value);
	}

	decrement() {
		this._eventBus.trigger(EVENT_SET_FORM_DIRTY);

		const factor = this.fieldValue.unit && this.fieldValue.unit.Factor || 1;
		if(this.field.MinValue !== undefined && this.editValue <= Math.ceil(this.field.MinValue / factor)) {
			this.setSpinnerValue(Math.ceil(this.field.MinValue / factor));
			return;
		}

		const value = !this.editValue && this.field.MinValue === 0 && !this.hasNumericConstraints()
			? "0"
			: getPreviousConstrainedValue(
				this.editValue,
				this.getNumericConstraints(),
				this.incrementingStep,
				this.digitCount
			);
		this.setSpinnerValue(value);
	}

	setSpinnerValue(number) {
		const formattedValue = typeof number === "number"
			? number.toFixed(this.digitCount)
			: number;
		if(!isValidCompleteNumber(formattedValue) || !this.isValueWithinConstraints(formattedValue)) {
			return;
		}

		this.fieldValue.number = formattedValue;
		this.editValue = formattedValue;
		this.previousEditValue = formattedValue;
		this.committedValueBeforeEdit = formattedValue;
		this.hasRejectedRangeEdit = false;
		this.clearNumberConstraintMessage();

		this.checkDirty();
	}

	handleKeyUp(event) {
		const regex = /[0-9.-\\t]/;
		const isValid = regex.test(event.key);

		if(!isValid) {
			event.preventDefault();
		}
	}

	handleBlur() {
		const editValue = this.editValue;
		let committedValue = this.committedValueBeforeEdit;

		if(editValue === "") {
			committedValue = "";
		}
		else if(!this.hasRejectedRangeEdit
			&& isValidCompleteNumber(editValue)
			&& this.isValueWithinConstraints(editValue)) {
			const normalizedValue = this.normalizeValue(editValue);
			committedValue = normalizedValue ?? editValue;
		}

		this.fieldValue.number = committedValue;
		this.editValue = committedValue;
		this.previousEditValue = committedValue;
		this.committedValueBeforeEdit = committedValue;
		this.isFocused = false;
		this.hasRejectedRangeEdit = false;
		this.clearNumberConstraintMessage();
		this.checkDirty();
	}

	onNumberChanged(newValue) {
		const candidate = newValue ?? "";
		const previousValue = this.previousEditValue;

		if(!isValidPartialNumber(candidate)) {
			this.editValue = previousValue;
			this.setNumberConstraintMessage("Enter a valid number.", "error");
			this.checkDirty(true, this.editValue);
			return;
		}

		if(!canCompleteNumberWithinConstraints(candidate, this.getNumericConstraints())) {
			this.editValue = previousValue;
			this.hasRejectedRangeEdit = true;
			if(!this.publishOnEdit) {
				this.fieldValue.number = this.committedValueBeforeEdit;
			}
			this.setNumberConstraintMessage("That value cannot be within the allowed range.", "error");
			this.checkDirty(true, this.editValue);
			return;
		}

		this.editValue = candidate;
		this.previousEditValue = candidate;
		this.hasRejectedRangeEdit = false;
		if(this.publishOnEdit) {
			this.fieldValue.number = candidate;
		}
		else if(candidate === ""
			|| (isValidCompleteNumber(candidate) && this.isValueWithinConstraints(candidate))) {
			const normalizedValue = candidate === "" ? "" : this.normalizeValue(candidate);
			this.fieldValue.number = normalizedValue ?? candidate;
		}

		if(candidate !== ""
			&& this.hasNumericConstraints()
			&& (!isValidCompleteNumber(candidate) || !this.isValueWithinConstraints(candidate))) {
			this.setNumberConstraintMessage(getNumericConstraintMessage(this.getNumericConstraints()), "warning");
		}
		else {
			this.clearNumberConstraintMessage();
		}

		this.checkDirty(true, candidate);
	}

	syncEditValue(value) {
		this.editValue = value;
		this.previousEditValue = value;
		this.committedValueBeforeEdit = value;
		this.lastSetValue = value;
		this.clearNumberConstraintMessage();
	}

	isValueWithinConstraints(value) {
		return isNumberWithinConstraints(value, this.getNumericConstraints());
	}

	getNumericConstraints() {
		return {
			minimumInclusive: this.field.MinimumInclusive,
			minimumExclusive: this.field.MinimumExclusive,
			maximumInclusive: this.field.MaximumInclusive,
			maximumExclusive: this.field.MaximumExclusive
		};
	}

	hasNumericConstraints() {
		const constraints = this.getNumericConstraints();
		return Object.values(constraints).some(value => value !== undefined);
	}

	normalizeValue(value) {
		const normalizedValue = !!this.roundToField
			? roundToRoundingPoint(value, this.field).toString()
			: roundToNearestIncrement(value, this.field).toString();

		return isValidCompleteNumber(normalizedValue) && this.isValueWithinConstraints(normalizedValue)
			? normalizedValue
			: undefined;
	}

	setNumberConstraintMessage(message, type) {
		this.numberConstraintMessage = message;
		this.numberConstraintMessageType = type;
		this.constraintMessage = message;
	}

	clearNumberConstraintMessage() {
		this.numberConstraintMessage = "";
		this.numberConstraintMessageType = "";
		this.constraintMessage = "";
	}

	getValidationStyling() {
		if(this.numberConstraintMessageType === "error") {
			return "validation-error";
		}
		if(this.numberConstraintMessageType === "warning") {
			return "validation-warning";
		}

		if(this.field.hasValidationError || this.hasRepeatError) {
			if(this.repeatId) {
				let isThisField = find(this.field.validationErrorRepeatIds, (id) => id === this.repeatId);
				isThisField = isThisField || this.hasRepeatError === true;
				return isThisField && "validation-error";
			}
			return "validation-error";
		}
		else if(this.field.hasValidationWarning) {
			if(this.repeatId) {
				const isThisField = find(this.field.validationWarningRepeatIds, (id) => id === this.repeatId);
				return isThisField && "validation-warning";
			}
			return "validation-warning";
		}

		return "";
	}

	checkDirty(skipUpdateTrigger = false, value = this.fieldValue?.number) {
		const isDirty = value !== this.initialFieldValue;
		this.setFieldDirty(this.field.Id, isDirty);

		if(skipUpdateTrigger === false) {
			this.triggerNumberValueChange();
		}
	}

	triggerNumberValueChange = (value) => {
		const newValue = value ?? this.fieldValue?.number;

		if(newValue !== this.lastSetValue) {
			const params = {
				field: this.field
			};

			this._eventBus.trigger(EVENT_VALUE_CHANGED(this.field.Id), params);

			this.lastSetValue = newValue;
		}
	}
}

export default angular
	.module("vesl.components.report-form.number-field", [])
	.component("numberField", {
		template,
		controller: Controller,
		bindings: {
			field: "<",
			fieldValue: "=",
			hasRepeatError: "<",
			pivotRow: "=",
			repeatId: "<",
			pivotColumn: "=",
			roundToField: "<",
			hideArrows: "<",
			setFieldDirty: "=",
			isInputDisabled: "<",
			constraintMessage: "=?",
			publishOnEdit: "<"
		},
		require: {
			reportScope: "^^reportScope"
		}
	}).name;
